import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { ArrowLeft, Briefcase, Trash2, Loader2, AlertTriangle, MapPin, Calendar, Edit3, Save } from 'lucide-react';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const AdminDirectHireJobs = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const superAdmin = isSuperAdmin && isSuperAdmin();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [purging, setPurging] = useState(false);
  const [editJob, setEditJob] = useState(null); // full job object being edited
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/agency/admin/direct-hire-jobs?limit=500');
      setJobs(res.data.jobs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load direct-booking jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id, title) => {
    if (!window.confirm(`Permanently delete "${title}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/agency/admin/direct-hire-jobs/${id}`);
      toast.success('Direct-booking job deleted');
      setJobs((prev) => prev.filter((j) => j.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const purgeAll = async () => {
    setPurging(true);
    try {
      const res = await api.post('/agency/admin/direct-hire-jobs/bulk-delete', { all_test_data: true });
      toast.success(res.data.message || 'Purged');
      setPurgeOpen(false);
      setPurgeConfirm('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const testJobCount = jobs.filter((j) => (j.title || '').toUpperCase().startsWith('TEST')).length;

  // Edit handlers
  const openEdit = (job) => {
    setEditJob({
      id: job.id,
      title: job.title || '',
      description: job.description || '',
      venue_name: job.venue_name || '',
      venue_address: job.venue_address || '',
      city: job.city || '',
      state: job.state || '',
      job_date: job.start_date || job.job_date || '',
      start_time: (job.start_time || '').slice(0, 5),
      end_time: (job.end_time || '').slice(0, 5),
      hourly_rate: job.hourly_rate ?? '',
      workers_needed: job.workers_needed ?? 1,
      status: job.status || 'open',
      dress_code: job.dress_code || '',
      responsibilities: job.responsibilities || '',
      arrival_instructions: job.arrival_instructions || '',
      requirements: job.requirements || '',
    });
  };

  const saveEdit = async () => {
    if (!editJob) return;
    setSaving(true);
    try {
      const { id, ...payload } = editJob;
      payload.hourly_rate = payload.hourly_rate === '' ? null : parseFloat(payload.hourly_rate);
      payload.workers_needed = parseInt(payload.workers_needed || 1, 10);
      await api.patch(`/agency/admin/direct-hire-jobs/${id}`, payload);
      toast.success('Direct-booking job updated');
      setEditJob(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100" data-testid="admin-direct-hire-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/admin/home')}
              data-testid="direct-hire-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">Direct-Booking Jobs</h1>
              <p className="text-sm text-gray-500">
                All agency direct-booking postings — {jobs.length} total
                {testJobCount > 0 && <span className="text-red-600 font-semibold"> · {testJobCount} TEST</span>}
              </p>
            </div>
          </div>
          {superAdmin && jobs.length > 0 && (
            <Button
              variant="outline"
              onClick={() => { setPurgeConfirm(''); setPurgeOpen(true); }}
              className="border-red-600 bg-red-50 text-red-700 hover:bg-red-100"
              data-testid="purge-all-direct-hire-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" /> Purge ALL ({jobs.length})
            </Button>
          )}
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">Listings</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Briefcase className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No direct-booking jobs in the system.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="direct-hire-list">
                {jobs.map((j) => {
                  const isTest = (j.title || '').toUpperCase().startsWith('TEST');
                  return (
                    <div
                      key={j.id}
                      className={`rounded-lg border p-3 sm:p-4 flex items-start justify-between gap-3 flex-wrap ${isTest ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-white'}`}
                      data-testid={`direct-hire-row-${j.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {isTest && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">TEST DATA</Badge>}
                          <Badge variant="outline" className="text-xs">{j.status}</Badge>
                          <Badge variant="outline" className="text-xs">{j.job_type || '—'}</Badge>
                        </div>
                        <div className="font-semibold text-gray-900 break-words">{j.title}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-3 mt-1 flex-wrap">
                          {j.agency_name && <span>Agency: <strong>{j.agency_name}</strong></span>}
                          {(j.city || j.state) && (
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{j.city} {j.state}</span>
                          )}
                          {j.start_date && (
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDateMMDDYYYY(j.start_date)}{j.end_date ? ` → ${formatDateMMDDYYYY(j.end_date)}` : ''}</span>
                          )}
                          {j.hourly_rate != null && <span>${Number(j.hourly_rate).toFixed(2)}/hr</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openEdit(j)}
                          className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                          data-testid={`edit-direct-hire-${j.id}`}
                        >
                          <Edit3 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => del(j.id, j.title)}
                          disabled={deletingId === j.id}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          data-testid={`delete-direct-hire-${j.id}`}
                        >
                          {deletingId === j.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <><Trash2 className="w-4 h-4 mr-1" /> Delete</>}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Purge ALL confirmation */}
      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent data-testid="purge-direct-hire-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Purge ALL direct-booking jobs?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>every direct-booking (agency) job</strong> and all their applications. Cannot be undone.
              Type <span className="font-mono text-red-700 font-bold">PURGE</span> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            value={purgeConfirm}
            onChange={(e) => setPurgeConfirm(e.target.value)}
            placeholder="Type PURGE"
            className="w-full rounded border border-red-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            data-testid="purge-confirm-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={purgeAll}
              disabled={purging || purgeConfirm !== 'PURGE'}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="confirm-purge-btn"
            >
              {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Purge All'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Direct-Hire Job */}
      <Dialog open={!!editJob} onOpenChange={(o) => !o && setEditJob(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-direct-hire-dialog">
          <DialogHeader>
            <DialogTitle>Edit Direct-Booking Job</DialogTitle>
          <DialogDescription className="sr-only">Edit Direct-Booking Job</DialogDescription>
          </DialogHeader>
          {editJob && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="space-y-2 md:col-span-2">
                <Label>Title</Label>
                <Input value={editJob.title} onChange={(e) => setEditJob({ ...editJob, title: e.target.value })} data-testid="edit-dh-title" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Description</Label>
                <Textarea rows={3} value={editJob.description} onChange={(e) => setEditJob({ ...editJob, description: e.target.value })} data-testid="edit-dh-description" />
              </div>
              <div className="space-y-2">
                <Label>Venue Name</Label>
                <Input value={editJob.venue_name} onChange={(e) => setEditJob({ ...editJob, venue_name: e.target.value })} data-testid="edit-dh-venue" />
              </div>
              <div className="space-y-2">
                <Label>Venue Address</Label>
                <Input value={editJob.venue_address} onChange={(e) => setEditJob({ ...editJob, venue_address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={editJob.city} onChange={(e) => setEditJob({ ...editJob, city: e.target.value })} data-testid="edit-dh-city" />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input maxLength={2} value={editJob.state} onChange={(e) => setEditJob({ ...editJob, state: e.target.value })} data-testid="edit-dh-state" />
              </div>
              <div className="space-y-2">
                <Label>Job Date</Label>
                <Input type="date" value={editJob.job_date} onChange={(e) => setEditJob({ ...editJob, job_date: e.target.value })} data-testid="edit-dh-date" />
              </div>
              <div className="space-y-2 grid grid-cols-2 gap-2">
                <div>
                  <Label>Start</Label>
                  <Input type="time" value={editJob.start_time} onChange={(e) => setEditJob({ ...editJob, start_time: e.target.value })} data-testid="edit-dh-start-time" />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="time" value={editJob.end_time} onChange={(e) => setEditJob({ ...editJob, end_time: e.target.value })} data-testid="edit-dh-end-time" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input type="number" step="0.01" min="0" value={editJob.hourly_rate}
                  onChange={(e) => setEditJob({ ...editJob, hourly_rate: e.target.value })}
                  data-testid="edit-dh-rate"
                />
              </div>
              <div className="space-y-2">
                <Label>Spots (talent needed)</Label>
                <Input type="number" min="1" value={editJob.workers_needed}
                  onChange={(e) => setEditJob({ ...editJob, workers_needed: e.target.value })}
                  data-testid="edit-dh-spots"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <select
                  className="w-full border rounded-md p-2 text-sm"
                  value={editJob.status}
                  onChange={(e) => setEditJob({ ...editJob, status: e.target.value })}
                  data-testid="edit-dh-status"
                >
                  <option value="open">Open</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="filled">Filled</option>
                  <option value="closed">Closed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Dress Code</Label>
                <Input value={editJob.dress_code} onChange={(e) => setEditJob({ ...editJob, dress_code: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Responsibilities</Label>
                <Textarea rows={2} value={editJob.responsibilities} onChange={(e) => setEditJob({ ...editJob, responsibilities: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Arrival Instructions</Label>
                <Textarea rows={2} value={editJob.arrival_instructions} onChange={(e) => setEditJob({ ...editJob, arrival_instructions: e.target.value })} />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Requirements</Label>
                <Textarea rows={2} value={editJob.requirements} onChange={(e) => setEditJob({ ...editJob, requirements: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditJob(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-direct-hire-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDirectHireJobs;
